import { useMemo } from 'react';
import { BarRounded, BarStackHorizontal } from '@visx/shape';
import { Group } from '@visx/group';
import { scaleBand } from '@visx/scale';
import { useTooltip } from '@visx/tooltip';
import { Axis, AxisLeft } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { localPoint } from '@visx/event';
import { ScaleLinear, ScaleOrdinal } from 'd3-scale';
import { truncate } from 'lodash';

import { common, model } from '@gosupersimple/types';

import { formatPropertyValue } from '@/explore/utils/format';
import { GroupFilter } from '@/explore/utils/drilldown';
import { InteractableProps } from '@/explore/utils/interactable';

import { ChartTooltipSingleValue } from '../common';
import { CategoryData } from '../grouped-chart/types';
import {
  explodeCombinedKey,
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  getSeriesAxes,
  getSeriesTotalsFormat,
  isCombinedKey,
} from '../grouped-chart/utils';
import { getMaxLabelWidth, getScreenPoint } from '../utils';
import { NumXTicks } from './grouped-horizontal-chart';
import { convertTicksToScale } from '../grouped-chart/utils/scale';

import commonStyles, { gridLineColor } from '../charts.module.scss';
import styles from './stacked-horizontal-bar-chart.module.scss';

type TooltipData = {
  categoryLabel: string;
  formattedCategoryValue: string;
  valueLabel: string;
  valueKey: string;
  value: number | null;
  color: string;
  type: model.PropertyType | null;
  format?: common.PropertyValueFormat;
};

interface HorizontalBarChartProps {
  categoryFormat: {
    precision?: string;
  };
  onCategoryClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  data: CategoryData;
  width: number;
  height: number;
  valueScales: {
    left: ScaleLinear<number, number>;
    right: ScaleLinear<number, number>;
  };
  colorScale: ScaleOrdinal<string, string>;
  showTotals: boolean;
}

const GroupWidth = parseInt(commonStyles.widthPerGroup);
const BarRadius = 5;
const CategoryPadding = 0.3;
const OuterPadding = 0.4;
const InnerPadding = 0.2;
const CategoryLabelMaxLength = 20;

export const StackedHorizontalBarChart = (props: HorizontalBarChartProps) => {
  const { data, width, height, valueScales, colorScale, onCategoryClick, showTotals } = props;
  const { showTooltip, hideTooltip, tooltipOpen, tooltipLeft, tooltipTop, tooltipData } =
    useTooltip<TooltipData>();

  const handleMouseOver = (event: any, data: TooltipData) => {
    const point = localPoint(event);
    if (point !== null) {
      const screenPoint = getScreenPoint(point, event.target);
      showTooltip({ tooltipLeft: screenPoint.x, tooltipTop: screenPoint.y, tooltipData: data });
    }
  };

  const axisTypes = getLeftRightAxisTypes(data.series);
  const axisFormats = getLeftRightAxisFormats(data.series);

  const formatBottomAxisValue = (n: number) => {
    const value = axisTypes.left === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });
  };

  const formatTopAxisValue = (n: number) => {
    const value = axisTypes.right === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });
  };

  const categoryScale = scaleBand({
    domain: data.items.map((d) => d.categoryValue.formatted),
    paddingOuter: OuterPadding,
    padding: CategoryPadding,
    range: [0, height],
  });

  const axes = useMemo(() => getSeriesAxes(data.series), [data.series]);

  const axisCategoryScale = scaleBand({
    domain: axes,
    paddingInner: axes.length > 1 ? InnerPadding : 0,
    range: [0, categoryScale.bandwidth()],
  });

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  const hasRightAxisSeries = data.series.some((series) => series.axis === 'right');

  const stackedData = data.items.map((d) => ({
    categoryLabel: d.label,
    formattedCategoryValue: d.categoryValue.formatted,
    ...d.values,
    ...data.series.reduce(
      (acc, series) => ({
        ...acc,
        [series.key]: d.values[series.key],
      }),
      {},
    ),
    stackTotals: {
      left: data.series
        .filter((s) => s.axis === 'left')
        .reduce<
          number | null
        >((acc, { key }) => (acc === null ? d.values[key] : acc + (d.values[key] ?? 0)), null),
      right: data.series
        .filter((s) => s.axis === 'right')
        .reduce<
          number | null
        >((acc, { key }) => (acc === null ? d.values[key] : acc + (d.values[key] ?? 0)), null),
    },
  }));

  // Default to top as main axis in case no series on bottom axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  // Force top axis ticks to align with primary axis ticks
  const topAxisTicks = convertTicksToScale(
    primaryValueScale.ticks(NumXTicks),
    primaryValueScale,
    valueScales.right,
  );

  const groupLabels = useMemo(() => {
    return data.items.map((item) => item.categoryValue.formatted);
  }, [data.items]);

  const dynamicGroupWidth = useMemo(() => {
    const labels = groupLabels.map((label) => truncate(label, { length: CategoryLabelMaxLength }));
    const maxWidth = getMaxLabelWidth(labels);
    return Math.min(Math.ceil(maxWidth), GroupWidth);
  }, [groupLabels]);

  const renderBarSeries = () => {
    return axes.map((axis) => {
      const keys = data.series.filter((s) => s.axis === axis).map(({ key }) => key);
      return (
        <BarStackHorizontal<Record<string, any>>
          key={`bar-stack-${axis}`}
          data={stackedData}
          keys={keys}
          y={(d) => d.formattedCategoryValue}
          width={width}
          xScale={valueScales[axis]}
          yScale={categoryScale}
          color={(x) => colorScale(x.toString())}>
          {(barStacks) => {
            return barStacks.map((barStack, barStackIdx) => {
              return barStack.bars
                .filter((bar) => bar.width > 0)
                .map((bar) => {
                  const series = data.series.find((s) => s.key === bar.key);
                  if (series === undefined) {
                    throw new Error(`Series not found for bar chart key: ${bar.key}`);
                  }
                  const barWidth = Math.max(1, bar.width); // very small clip paths misbehave in chrome, see SUP-3580
                  return (
                    <BarRounded
                      {...InteractableProps}
                      key={`${barStack.index}-${bar.index}`}
                      radius={BarRadius}
                      x={bar.x}
                      y={bar.y + (axisCategoryScale(axis) ?? 0)}
                      width={barWidth}
                      height={axisCategoryScale.bandwidth()}
                      fill={bar.color}
                      right={barStackIdx === barStacks.length - 1}
                      onMouseMove={(e) => {
                        return handleMouseOver(e, {
                          value: Number(bar.bar.data[bar.key]),
                          valueKey: bar.key.toString(),
                          categoryLabel: bar.bar.data.categoryLabel,
                          formattedCategoryValue: bar.bar.data.formattedCategoryValue,
                          valueLabel: series?.label ?? bar.key.toString(),
                          color: bar.color,
                          type: series?.type ?? 'Number',
                          format: series?.format,
                        });
                      }}
                      onMouseOut={hideTooltip}
                      onClick={(e) => {
                        const item = data.items.at(bar.index);
                        if (item !== undefined) {
                          onCategoryClick?.({
                            x: e.clientX,
                            y: e.clientY,
                            key: isCombinedKey(series.key)
                              ? explodeCombinedKey(series.key).valueKey
                              : series.key,
                            groupFilters: [
                              ...(isCombinedKey(series.key)
                                ? [
                                    {
                                      key: explodeCombinedKey(series.key).groupKey,
                                      value: explodeCombinedKey(series.key).groupValue,
                                    },
                                  ]
                                : []),
                              {
                                key: item.key,
                                value: item.categoryValue,
                              },
                            ],
                          });
                        }
                      }}
                    />
                  );
                });
            });
          }}
        </BarStackHorizontal>
      );
    });
  };

  const renderSeriesValues = () => {
    return axes.map((axis) => {
      const keys = data.series.filter((s) => s.axis === axis).map(({ key }) => key);
      return (
        <BarStackHorizontal<Record<string, any>>
          key={`bar-values-${axis}`}
          data={stackedData}
          keys={keys}
          y={(d) => d.formattedCategoryValue}
          width={width}
          xScale={valueScales[axis]}
          yScale={categoryScale}
          color={(x) => colorScale(x.toString())}>
          {(barStacks) => {
            return barStacks.map((barStack) => {
              return barStack.bars
                .filter((bar) => bar.width > 0)
                .map((bar) => {
                  const series = data.series.find((s) => s.key === bar.key);
                  const value = bar.bar.data[bar.key];
                  if (series === undefined || !series.showValues || value === null) {
                    return null;
                  }
                  return (
                    <text
                      key={`${barStack.index}-${bar.index}`}
                      className={styles.valueLabel}
                      x={bar.x + bar.width / 2}
                      y={bar.y + (axisCategoryScale(axis) ?? 0)}
                      dy={1 + axisCategoryScale.bandwidth() / 2}
                      textAnchor="middle">
                      {formatPropertyValue(value, {
                        type: series.type,
                        format: series.format,
                        style: 'compact',
                      })}
                    </text>
                  );
                });
            });
          }}
        </BarStackHorizontal>
      );
    });
  };

  const renderTotalValues = () => {
    if (!showTotals) {
      return null;
    }

    const { type, format } = getSeriesTotalsFormat(data.series);

    return stackedData.map((item) => {
      return axes.map((axis) => {
        const axisTotal = item.stackTotals[axis];
        const valueScale = valueScales[axis];
        if (axisTotal === null) {
          return null;
        }
        return (
          <text
            key={`bar-total-${axis}-${item.formattedCategoryValue}`}
            className={styles.valueLabel}
            x={valueScale(axisTotal)}
            y={(categoryScale(item.formattedCategoryValue) ?? 0) + (axisCategoryScale(axis) ?? 0)}
            dx={7}
            dy={1 + axisCategoryScale.bandwidth() / 2}>
            {formatPropertyValue(axisTotal, {
              type,
              format,
              style: 'compact',
            })}
          </text>
        );
      });
    });
  };

  return (
    <div className={commonStyles.graph}>
      {tooltipData !== undefined ? (
        <ChartTooltipSingleValue
          position={{ x: tooltipLeft ?? 0, y: tooltipTop ?? 0 }}
          tooltipData={{
            label: tooltipData.formattedCategoryValue,
            value: tooltipData.value,
            type: tooltipData.type,
            format: tooltipData.format,
          }}
          tooltipOpen={tooltipOpen}
          categoryLabel={tooltipData.categoryLabel}
          valueLabel={tooltipData.valueLabel}
          seriesColor={tooltipData.color}
        />
      ) : null}
      <svg width={width} height={height}>
        <Group left={dynamicGroupWidth}>
          <GridColumns
            width={width}
            height={height}
            scale={primaryValueScale}
            stroke={gridLineColor}
            tickValues={primaryValueScale.ticks(NumXTicks)}
          />
          {renderBarSeries()}
          {renderSeriesValues()}
          {renderTotalValues()}

          <AxisLeft
            scale={categoryScale}
            stroke={gridLineColor}
            hideTicks
            tickClassName={styles.axisLabel}
            tickLabelProps={{
              fill: '#FFF',
              fontSize: 12,
              textAnchor: 'end',
              width: 100,
            }}
            tickValues={categoryScale.domain()} // Always render all tick values
            tickComponent={({ formattedValue, ...tickProps }) => {
              return (
                <text {...tickProps}>
                  <title>{formattedValue}</title>
                  {truncate(formattedValue, { length: CategoryLabelMaxLength })}
                </text>
              );
            }}
          />

          {domainSizes.left > 0 && (
            <Axis
              left={5}
              top={height - 25}
              hideTicks
              scale={valueScales.left}
              tickClassName={commonStyles.tickLabel}
              tickLabelProps={() => ({ textAnchor: 'start' })}
              tickFormat={(n) => formatBottomAxisValue(n.valueOf())}
              tickValues={valueScales.left.ticks(NumXTicks)}
              hideAxisLine
            />
          )}
          {domainSizes.right > 0 && hasRightAxisSeries && (
            <Axis
              left={6}
              top={-4}
              hideTicks
              scale={valueScales.right}
              tickClassName={commonStyles.tickLabel}
              tickLabelProps={() => ({ textAnchor: 'start' })}
              tickFormat={(n) => formatTopAxisValue(n.valueOf())}
              tickValues={topAxisTicks}
              hideAxisLine
            />
          )}
        </Group>
      </svg>
    </div>
  );
};
